import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useTranslations } from "@vocab/react";
import { Columns, Column, Stack, MenuRenderer, MenuItemLink, Strong, IconTick, Box, IconLocation, Inline, Text, Divider, IconPlatformAndroid, IconPlatformApple, IconSocialFacebook, IconSocialInstagram, IconSocialX, IconSocialYouTube } from "braid-design-system";
import { useCallback, useRef } from "react";
import { brandDisplayNameMap, useCountryList, AUTHENTICATION_TYPES, useUrls, generateAuthenticatedUrl, useCountries, getUserProfileLink, DEFAULT_COUNTRY, PageBlock } from "./styles/ApacCandidateHeader/ApacCandidateHeader.mjs";
import { ApacCandidateHeader } from "./styles/ApacCandidateHeader/ApacCandidateHeader.mjs";
import { useLink, ChevronIconWrapper, CommonContextProvider, Accordion } from "./styles/ApacCandidateFooter/components/Accordion/Accordion.mjs";
import { LinkProvider, DATA_HEADER_FOOTER_LINK_CONTEXT_KEY, linkContextDestructor, mapLinkActionHelper } from "./styles/ApacCandidateFooter/components/Accordion/Accordion.mjs";
import { translations as translations$2 } from "./ApacCandidateFooter/.vocab/index.mjs";
import { useMelwaysInfo, useMelwaysLink } from "@seek/melways-react";
import { CONTENT_SPACE, FooterSegment } from "./styles/ApacCandidateFooter/components/FooterSegment/FooterSegment.mjs";
import { FooterLink } from "./styles/ApacCandidateFooter/components/FooterLink/FooterLink.mjs";
import { translations } from "./ApacCandidateFooter/components/LocationSwitcher/.vocab/index.mjs";
import { translations as translations$1 } from "./ApacCandidateFooter/components/LowerFooter/.vocab/index.mjs";
import { translations as translations$3 } from "./.vocab/index.mjs";
const LegalLinks = ({ links, space, collapseBelow }) => /* @__PURE__ */ jsx(Columns, { space, collapseBelow, children: links.map((link) => /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(FooterLink, { link, size: "xsmall" }) }, link.title)) });
const LocationSwitcher = ({ list, activeCountryText }) => {
  const { sendLinkEvent, injectLinkContextToData } = useLink();
  const { t } = useTranslations(translations);
  return /* @__PURE__ */ jsx(Stack, { space: "small", children: /* @__PURE__ */ jsx(
    MenuRenderer,
    {
      placement: "top",
      width: "medium",
      offsetSpace: "small",
      onOpen: () => {
        sendLinkEvent({
          linkContext: {
            openingOrClosing: "opening",
            source: activeCountryText,
            component: { name: "country", text: activeCountryText }
          }
        });
      },
      onClose: ({ reason }) => {
        if (reason === "exit") {
          sendLinkEvent({
            linkContext: {
              openingOrClosing: "closing",
              source: activeCountryText
            }
          });
        }
      },
      data: {
        automation: "country-switcher-menu"
      },
      trigger: (triggerProps, { open }) => /* @__PURE__ */ jsx(
        Box,
        {
          userSelect: "none",
          cursor: "pointer",
          ...triggerProps,
          id: "country-switcher-menu",
          component: "button",
          data: { testid: "country-switcher-menu" },
          "aria-label": t("Location"),
          children: /* @__PURE__ */ jsx(
            ChevronIconWrapper,
            {
              icon: IconLocation,
              label: activeCountryText,
              size: "small",
              upDirectionCondition: !open
            }
          )
        }
      ),
      reserveIconSpace: true,
      children: list.map(({ isActive, text, href, linkContext: linkContext2 }, index) => /* @__PURE__ */ jsx(
        MenuItemLink,
        {
          href,
          data: injectLinkContextToData(linkContext2, {
            automation: linkContext2.source
          }),
          icon: isActive ? /* @__PURE__ */ jsx(IconTick, {}) : void 0,
          children: isActive ? /* @__PURE__ */ jsx(Strong, { children: text }) : text
        },
        `countrySwitcher_link_${index}`
      ))
    }
  ) });
};
const LowerFooter = ({
  localisations,
  showCountryList,
  isFooterExpanded: isFooterExpandedFlag
}) => {
  const isFooterExpandable = isFooterExpandedFlag !== void 0;
  const isFooterExpanded = isFooterExpandedFlag || !isFooterExpandable;
  const CONTENT_COLLAPSE_BELOW = isFooterExpanded ? "wide" : "tablet";
  const { list: countryList, activeCountryText } = localisations.countryList;
  const { t } = useTranslations(translations$1);
  const { brand } = useMelwaysInfo();
  const brandDisplayName = brandDisplayNameMap[brand];
  return /* @__PURE__ */ jsxs(
    Box,
    {
      display: "flex",
      alignItems: {
        mobile: "center",
        tablet: isFooterExpanded ? "flexStart" : "flexEnd",
        wide: "flexEnd"
      },
      justifyContent: {
        mobile: "flexStart",
        tablet: showCountryList ? "spaceBetween" : "flexEnd"
      },
      flexDirection: {
        mobile: "row",
        tablet: isFooterExpanded ? "column" : "row",
        wide: "row"
      },
      flexWrap: {
        wide: "wrap"
      },
      children: [
        /* @__PURE__ */ jsx(
          Box,
          {
            display: {
              mobile: "none",
              tablet: showCountryList ? "block" : "none"
            },
            paddingBottom: {
              tablet: isFooterExpanded ? CONTENT_SPACE : "none",
              wide: "none"
            },
            children: /* @__PURE__ */ jsx(
              LocationSwitcher,
              {
                activeCountryText,
                list: countryList
              }
            )
          }
        ),
        /* @__PURE__ */ jsxs(Inline, { space: CONTENT_SPACE, collapseBelow: CONTENT_COLLAPSE_BELOW, children: [
          isFooterExpanded && /* @__PURE__ */ jsx(
            LegalLinks,
            {
              space: CONTENT_SPACE,
              collapseBelow: CONTENT_COLLAPSE_BELOW,
              links: localisations.legalLinks
            }
          ),
          /* @__PURE__ */ jsx(Text, { size: "xsmall", tone: "secondary", children: t(`copyright ${brand}`, {
            brand: brandDisplayName,
            year: (/* @__PURE__ */ new Date()).getFullYear().toString()
          }) })
        ] })
      ]
    }
  );
};
const FirstNationsArtwork = () => /* @__PURE__ */ jsxs(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    role: "img",
    "aria-label": "First Nations artwork",
    width: "100",
    height: "82",
    fill: "none",
    children: [
      /* @__PURE__ */ jsx("title", { children: "Artwork of a gum leaf symbolising ‘welcome’ and acknowledging diverse countries, environments, communities, cultures, beliefs, and history." }),
      /* @__PURE__ */ jsx(
        "path",
        {
          fill: "#afa68f",
          d: "m85.713 56.908.854-.017.723-.078-.651-3.189c-.023 0-.047.006-.072.006l-.532.01h-.086a8.1 8.1 0 0 1-3.519-.849l-1.468 2.988a11.1 11.1 0 0 0 4.752 1.128zm-.651-1.674c.031-.47.265-.697.692-.677.421-.033.636.193.655.699.047.5-.183.751-.688.724q-.743-.042-.659-.745zm-1.597-1.017c.417.08.569.347.454.843-.086.494-.37.685-.846.525-.474-.154-.626-.453-.448-.892.152-.445.437-.605.84-.476m7.393-12.665q.407.39.716.87l2.776-1.452a8.5 8.5 0 0 0-1.427-1.791c-1.696-1.622-4.003-2.502-6.567-2.527l-.314 3.401c1.969-.119 3.679.412 4.816 1.499m.731-1.38q.239-.294.491-.304a.5.5 0 0 1 .259.064.7.7 0 0 1 .255.217c.339.394.314.732-.07 1.005-.368.293-.694.281-.963-.029-.07-.078-.135-.14-.174-.213a.43.43 0 0 1-.056-.224c.004-.156.088-.332.257-.517zm-2.15-1.282c.162-.474.478-.615.932-.377.45.248.546.574.285.968-.238.406-.546.509-.909.31-.392-.143-.497-.431-.308-.902zm-2.443-.525q.013-.378.205-.543a.5.5 0 0 1 .238-.109.7.7 0 0 1 .326.014q.746.137.566.823-.146.692-.77.584a1.2 1.2 0 0 1-.273-.062.4.4 0 0 1-.185-.142c-.094-.127-.133-.314-.107-.566l.002.002zm5.479 7.672c-.056 2.908-1.279 5.337-3.145 6.622l1.585 2.808c2.841-1.813 4.695-5.181 4.773-9.188a11 11 0 0 0-.094-1.665l-3.149.572a8 8 0 0 1 .027.851h.002zM91.66 53.7a1 1 0 0 1-.269.193.6.6 0 0 1-.267.043q-.256-.026-.464-.308c-.152-.181-.218-.353-.213-.509a.5.5 0 0 1 .068-.224c.041-.07.094-.152.17-.226q.209-.236.45-.222c.241.014.326.088.509.261.191.162.296.33.302.498q0 .248-.287.498zm1.835-7.761c.115-.105.298-.162.552-.162q.378-.022.56.152c.182.174.185.302.148.546q-.05.737-.753.646-.704-.057-.669-.703c.006-.217.045-.373.16-.48zm-.507 2.787c.07-.424.351-.576.842-.465q.746.126.53.837c-.054.24-.187.394-.333.472s-.335.07-.556-.014q-.335-.11-.452-.318c-.084-.14-.072-.302-.029-.513zm.049 1.903c.228.103.38.234.427.392s.004.344-.131.554a1 1 0 0 1-.199.259.6.6 0 0 1-.242.117c-.168.035-.345-.021-.534-.16q-.295-.194-.353-.424a.5.5 0 0 1 0-.236.8.8 0 0 1 .097-.267c.092-.193.213-.306.371-.344s.337-.008.564.109v-.002z"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          fill: "#9a8479",
          d: "M97.959 41.908c-.616-1.879-1.634-3.509-3.024-4.838-2.217-2.117-5.18-3.277-8.442-3.331l-.062 1.604c2.872.041 5.469 1.046 7.397 2.888l.324.326.103.111.205.228.115.137.177.219.113.146.164.222.107.152.156.234.096.148.158.266.076.129.215.404.209.441.209.507.061.17.123.351.062.205.095.33.057.218.043.164.064.293a12 12 0 0 1 .238 2.672c-.051 2.64-.799 5.033-2.065 6.971l-.175.263-.096.131-.173.238-.199.256-.185.226-.21.242-.197.217-.222.23-.207.203-.234.217-.216.191-.248.203-.224.178-.259.189-.234.164-.271.174-.24.15-.283.16-.246.135-.294.145-.251.119-.308.129-.257.103-.324.113-.257.088-.339.096-.255.07-.365.08-.246.053-.415.064-.209.031-.638.061-.803.021q-.079 0-.16-.002l-.669-.025-.218-.02-.445-.043-.254-.035-.402-.062-.263-.049-.388-.084-.257-.062-.388-.107-.244-.07-.415-.139-.203-.07-.597-.236-.614-.283-.799 1.384a14 14 0 0 0 6.124 1.507l1.098-.023c6.717-.445 11.697-6.104 11.841-13.458a13.3 13.3 0 0 0-.641-4.428h.004zM22.721 69.405l-.253.277-.23.244-.269.264-.244.23-.285.248-.253.215-.3.232-.263.199-.316.217-.273.181-.332.199-.281.164-.349.181-.285.144-.372.166-.283.123-.402.15-.273.102-.456.139-.236.07-.71.168-.485.086-.402.053-.209.016-.339.025-.415.014h-.337l-.415-.02-.332-.023-.415-.053-.322-.047-.421-.086-.308-.068-.431-.125-.285-.086-.468-.176-.232-.09-.684-.32a10 10 0 0 1-1.111-.664 11.3 11.3 0 0 1-2.47-2.338c-1.371-1.745-2.332-3.94-2.747-6.356l-.113-.722-.051-.441c-.322-3.28.599-6.313 2.591-8.538l.314-.334.109-.105.22-.213.133-.119.213-.185.142-.117.216-.17.148-.111.228-.164.146-.101.259-.166.127-.08.396-.228.435-.222.499-.224.168-.066.347-.135.203-.07.328-.105.216-.062.162-.047.29-.074a12 12 0 0 1 2.661-.324l.79.014-.345-1.614-.464-.004a13.3 13.3 0 0 0-4.401.783 11.6 11.6 0 0 0-4.736 3.179c-2.295 2.564-3.357 6.032-2.993 9.765a16 16 0 0 0 .181 1.288c.708 4.104 2.854 7.652 5.888 9.738 2.611 1.793 5.746 2.438 8.832 1.813a13 13 0 0 0 1.905-.535 13.76 13.76 0 0 0 5.387-3.606l-.94-1.347v-.004z"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          fill: "#afa68f",
          d: "m18.287 67.295 1.913 2.707a11.2 11.2 0 0 0 1.774-1.731l-1.876-3.107a8.4 8.4 0 0 1-1.811 2.129zm2.73.412c.012.164-.057.349-.248.513q-.527.534-.996-.01c-.322-.336-.333-.663-.039-.968.144-.158.273-.254.435-.256s.333.074.524.24q.3.23.324.48zm-13.158-5.64-.012-.064-.123-.861c-.195-1.989.281-3.737 1.339-4.92a5 5 0 0 1 .846-.744l-1.538-2.732a8.6 8.6 0 0 0-1.745 1.485c-1.745 1.95-2.548 4.627-2.264 7.539l.15 1.062.218 1.03 3.311-.905-.185-.888zm-2.517-2.629q.113-.751.805-.593c.464.08.667.334.608.751a1.3 1.3 0 0 1-.053.275.4.4 0 0 1-.136.189c-.123.098-.31.143-.562.125q-.378-.001-.548-.187a.5.5 0 0 1-.117-.234.7.7 0 0 1 .004-.326h-.002zm1.382 2.98c-.096.125-.265.211-.515.252q-.369.082-.579-.053c-.21-.135-.211-.265-.232-.507-.031-.242 0-.429.105-.556q.167-.195.521-.211c.234-.025.413.016.536.111.129.096.214.248.246.463.047.213.016.379-.084.505zm.618-5.027c-.129.396-.415.511-.891.336-.48-.146-.63-.457-.406-.921.234-.459.556-.564.957-.316.413.226.526.531.339.9v.002zm.002-2.338a.5.5 0 0 1 .057-.261.7.7 0 0 1 .209-.264q.574-.526 1.006.039c.304.359.302.685 0 .964-.076.072-.136.139-.207.18s-.142.063-.222.064c-.156 0-.333-.076-.524-.242-.199-.154-.308-.312-.32-.482zm6.288-3.815a11 11 0 0 0-1.659.146l.671 3.13.848-.055a10 10 0 0 1 2.16.203l-.932-3.39-1.088-.037zm.213 2.352c-.216 0-.374-.033-.484-.146s-.172-.295-.179-.546q-.033-.377.135-.566c.168-.189.296-.195.54-.166q.737.026.669.732c-.021.47-.252.701-.68.691v.002zm1.133 15.324c-1.324.267-2.57.01-3.699-.767-.846-.582-1.577-1.417-2.156-2.434l-3.042 1.518c.809 1.405 1.862 2.588 3.106 3.443a8.58 8.58 0 0 0 6.612 1.36l.712-.172-.87-3.115-.661.168zm-6.306-.921c-.363.345-.708.326-.994-.088s-.226-.74.177-.984q.586-.396.955.125c.287.314.248.619-.14.947zm1.913 1.612c-.24.441-.575.523-.983.209-.4-.318-.437-.654-.115-.999.302-.361.622-.41.95-.152.361.209.413.511.148.943zm2.349.88q-.096.755-.85.537c-.495-.156-.643-.476-.456-.906.166-.445.444-.587.836-.466.41.058.562.34.47.833zm1.915.757a2 2 0 0 1-.179.012 1 1 0 0 1-.158-.029.6.6 0 0 1-.242-.129c-.127-.115-.183-.293-.177-.527-.008-.236.045-.414.146-.535a.5.5 0 0 1 .191-.137 1 1 0 0 1 .125-.037q.068-.021.144-.033c.406-.09.651.117.743.613q.179.74-.593.802"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          fill: "#a97455",
          d: "m95.275 43.098-2.999 1.007.088.433 3.123-.566-.214-.872zm-.611-1.556-2.776 1.452.201.482 2.979-1.001a9 9 0 0 0-.405-.933zM87.286 53.53l.645 3.165a9 9 0 0 0 .946-.258l-1.133-3.021a5 5 0 0 1-.458.111v.002zm1.072-.334 1.129 3.011q.443-.184.864-.412l-1.581-2.8-.411.199v.002zm-.605-8.245c-.308-.351-.858-.566-1.55-.605a4.6 4.6 0 0 0-.918.047 42 42 0 0 1-1.234 4.333c.556.291 1.148.439 1.65.406q.166-.011.318-.049c1.735-.445 2.012-2.365 2.057-2.945.039-.5-.07-.9-.32-1.187h-.002zM16.254 68.527l.877 3.144a10 10 0 0 0 1.02-.414l-1.534-2.888zm.941-.463 1.54 2.896.938-.574-1.903-2.693zm-8.979-4.476-3.311.906.267.806 3.207-1.212-.164-.498zm2.25-11.834 1.1 2.968.431-.103-.665-3.107zm-1.53.661 1.538 2.73.474-.217-1.094-2.949a9 9 0 0 0-.918.435zm-3.52 13.49.353.753 3.049-1.522-.201-.443zm11.381-4.764a2 2 0 0 0-.058-.316c-.499-1.721-2.427-1.938-3.005-1.963-.501-.021-.895.1-1.174.359-.341.32-.538.876-.556 1.569-.027 1.194.489 2.574 1.23 3.286.359.343.733.505 1.111.48.626-.041 1.271-.476 1.772-1.192.491-.703.745-1.553.68-2.223h-.002z"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          fill: "#dad7c4",
          d: "M89.956 42.498c-.881-.841-2.26-1.245-3.882-1.138l-.215.02-.291 1.667c.248-.016.48-.018.708-.004 1.061.059 1.911.422 2.461 1.05.484.552.706 1.296.641 2.147-.16 2.098-1.322 3.673-3.036 4.112-.829.211-1.817.064-2.739-.383l-.649 1.63a6.84 6.84 0 0 0 3.012.732h.099a4 4 0 0 0 .415-.006c2.648-.176 4.619-2.83 4.687-6.311.029-1.51-.39-2.726-1.213-3.513v-.002zm-2.667-3.425a1.4 1.4 0 0 0 .273.063c.413.072.675-.123.77-.584q.18-.687-.565-.824c-.123-.033-.232-.033-.326-.014s-.174.055-.238.109c-.129.109-.193.291-.205.543q-.034.377.107.566a.43.43 0 0 0 .185.142zm2.457.72c.363.199.671.096.909-.31.261-.392.166-.718-.285-.968-.454-.238-.77-.099-.932.377-.189.47-.084.759.308.902zm1.816 1.334c.269.31.595.322.963.029.384-.273.409-.611.07-1.005a.7.7 0 0 0-.255-.217.54.54 0 0 0-.259-.064c-.17.006-.331.109-.491.304-.17.185-.254.361-.257.517a.46.46 0 0 0 .057.224c.039.07.103.135.174.213v-.002zm3.042 4.803c-.121-.117-.308-.164-.56-.152-.251 0-.435.057-.552.162s-.156.261-.16.48q-.035.646.669.703c.468.063.719-.156.753-.646.037-.242-.027-.429-.148-.547zm-.774 2.33c-.491-.111-.772.041-.842.465-.043.211-.055.375.029.513q.116.21.452.318.33.127.556.014a.7.7 0 0 0 .333-.472q.217-.713-.53-.837zm-.794 2.369c-.226-.117-.407-.148-.563-.109s-.279.15-.371.343q-.075.142-.097.267c-.022.125-.02.16 0 .236.039.152.154.297.353.423q.286.209.534.16a.6.6 0 0 0 .242-.117 1 1 0 0 0 .199-.26q.203-.318.131-.554c-.072-.236-.201-.289-.427-.392v.002zm-1.39 2.075c-.183-.176-.351-.258-.509-.261s-.31.063-.45.222a1 1 0 0 0-.17.226.45.45 0 0 0-.068.224c-.008.158.06.33.213.509q.21.285.464.308a.6.6 0 0 0 .267-.043.9.9 0 0 0 .269-.193c.191-.164.289-.334.287-.498s-.111-.336-.302-.498v.002zm-5.891 1.85c-.425-.02-.659.207-.692.677q-.085.703.659.745c.505.029.735-.224.688-.724-.02-.505-.234-.732-.655-.699zm-2.29-.338c-.404-.129-.688.031-.84.476-.177.437-.025.738.448.892s.76-.031.846-.525c.115-.494-.037-.761-.454-.843m-66.278 9.9c-.729 1.042-1.733 1.678-2.757 1.747-.749.051-1.476-.242-2.102-.841-.868-.831-1.472-2.203-1.605-3.569a6 6 0 0 1-.025-.693c.025-1.062.361-1.924.969-2.494.536-.502 1.271-.748 2.123-.71 1.41.063 2.597.587 3.375 1.438l-1.043-2.857a8.5 8.5 0 0 0-2.615-.365c-1.511.018-2.71.476-3.469 1.325-.811.907-1.172 2.299-1.012 3.921l.107.751.014.074c.398 2.344 1.445 4.249 2.872 5.228a3.42 3.42 0 0 0 2.702.564c2.045-.412 3.759-1.916 4.636-3.845l-1.246-2.568c.037.937-.288 1.987-.922 2.894h-.004zm-2.488 5.715a1 1 0 0 0-.144.033 1 1 0 0 0-.125.037.5.5 0 0 0-.191.137c-.101.121-.154.299-.146.535q-.01.354.177.527a.6.6 0 0 0 .242.129.8.8 0 0 0 .158.029 2 2 0 0 0 .179-.012q.773-.063.593-.802c-.092-.496-.335-.701-.743-.613m-2.234-.178c-.392-.121-.671.022-.836.466-.187.429-.039.747.456.905.501.146.784-.037.85-.537.092-.496-.06-.775-.47-.833v-.002zm-2.025-.986q-.494-.388-.95.152c-.322.345-.285.681.115.999.407.314.743.232.983-.209.265-.431.215-.736-.148-.943zm-2.582-1.743q-.607.368-.177.984c.43.616.63.433.994.088.386-.328.425-.632.14-.947-.246-.345-.565-.39-.955-.125zm-1.297-5.473q-.185-.145-.536-.111-.353.016-.521.211c-.105.129-.136.316-.105.556.019.244.096.416.232.507s.333.107.579.053q.375-.063.515-.252.15-.189.084-.505c-.031-.215-.117-.367-.246-.462zm-.558-1.266q.378.023.562-.125a.4.4 0 0 0 .136-.189c.027-.078.041-.17.053-.275.058-.416-.144-.671-.608-.751q-.69-.156-.805.593a.7.7 0 0 0-.004.326.5.5 0 0 0 .117.234q.17.186.548.187zm1.001-3.693q-.604-.372-.957.316c-.224.462-.074.775.406.921.476.174.762.06.891-.336.187-.369.074-.673-.339-.9zm.661-.958c.191.164.368.242.524.242a.4.4 0 0 0 .222-.064c.07-.041.131-.107.207-.179.3-.279.302-.605 0-.964-.285-.377-.624-.39-1.006-.039a.7.7 0 0 0-.209.263.55.55 0 0 0-.057.261q.02.253.32.482l-.002-.002zm5.697-2.088c.109.113.267.146.484.146.429.01.659-.22.68-.691q.07-.705-.669-.732c-.244-.029-.429.041-.54.166s-.154.314-.135.566q.014.377.179.546v-.002zm7.331 13.78q-.287-.247-.524-.24c-.237.007-.289.098-.435.256-.296.304-.283.63.039.968.312.363.643.363.996.01.191-.162.259-.347.248-.513s-.125-.326-.324-.48z"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          fill: "#c7b470",
          d: "m34.942 75.777-.653-.699c-.406-.445-.959-.371-1.232.166l-.472.896-.226.422c-.259.459-.09 1.145.376 1.528 0 0 .807.662 1.375.585.577-.08 1.205-.972 1.205-.972.347-.492.326-1.202-.043-1.579l-.329-.349zm6.672-22.219a39.6 39.6 0 0 1-4.014-12.82 39.6 39.6 0 0 1 .606-13.973c.178-.788.314-1.284.314-1.284.162-.58.105-1.509-.127-2.065l-1.063-2.412c-.257-.544-.639-.529-.848.037 0 0-.066.18-.271.79a42.8 42.8 0 0 0-1.839 19.338 42.8 42.8 0 0 0 6.516 17.636l.448.681c.345.494.723.414.842-.178l.435-2.543c.09-.595-.08-1.512-.376-2.035 0 0-.255-.453-.626-1.173h.002zM18.835 16.541c4.081 4.783 8.844 15.583 8.844 15.583.244.55.513.513.6-.082 0 0 1.657-11.297 4.19-16.85l.22-.484c.254-.527.177-1.36-.172-1.85l-1.035-1.431c-.361-.482-.866-.47-1.123.023l-.222.457c-1.252 2.572-2.763 6.988-2.763 6.988-.195.57-.472 1.431-.614 1.916s-.478.529-.743.1a56 56 0 0 0-1.098-1.684s-2.767-4.067-4.748-6.294l-.335-.377c-.38-.402-.873-.273-1.094.287l-.567 1.497c-.207.566-.062 1.378.322 1.803zl-.002-.002zm3.853-9.586 1.406 1.485c.406.445.959.369 1.23-.168l.768-1.454.234-.427c.273-.461.183-1.218-.199-1.682 0 0-1.88-2.279-2.443-2.203h-.002c-.421.06-1.566 2.432-1.566 2.432-.261.543-.16 1.294.226 1.669l.343.349h.002zm-5.69 16.924-.458-.656c-.357-.484-.735-.394-.838.199l-.384 2.51c-.078.597.115 1.507.429 2.02l.612 1.064a39.6 39.6 0 0 1 4.621 13.956c.259 1.928.371 3.844.351 5.736a39 39 0 0 1-.049 1.577l-.137 1.823-.174 1.505-.179 1.169a37 37 0 0 1-.273 1.473l-.168.775-.076.326-.213.853a3.5 3.5 0 0 0 .162 2.051l.955 1.987.123.256c.275.535.669.511.875-.055l.25-.74.068-.217.384-1.247.174-.607.189-.718.238-.974.175-.781.031-.15a43 43 0 0 0 .367-2.004l.294-2.236.156-1.842a43 43 0 0 0-.3-8.392 42.75 42.75 0 0 0-7.208-18.657l.002-.002zm21.896 42.265c-3.675-4.551-7.998-14.556-7.998-14.556-.238-.552-.505-.517-.591.078 0 0-.548 3.807-1.493 8.05-.661 2.97-1.517 6.153-2.515 8.393l-.215.482c-.185.388-.181.943-.023 1.401.055.158.129.306.22.431l.877 1.171c.366.478.869.457 1.117-.045l.218-.461c1.141-2.412 2.519-6.467 2.519-6.467a56 56 0 0 0 .612-1.916c.142-.484.478-.529.743-.099s.758 1.188 1.096 1.686c0 0 2.355 3.487 4.063 5.488l.331.388c.372.421.86.31 1.084-.25l.593-1.534c.213-.564.074-1.388-.304-1.833l-.333-.414zM52.909 77.14c-.016-.486-.398-.732-.846-.544l-.76.304-.359.142c-.4.146-.655.66-.569 1.138 0 0 .15.831.54 1.081.396.254 1.252.033 1.252.033.472-.121.831-.57.799-.993l-.02-.388zm29.639-32.109-.581.345-.817.507-1.811 1.247-1.807 1.425-1.583 1.419-1.306 1.317-1.141 1.274-.944 1.153-1.843 2.597a34.6 34.6 0 0 0-5.222 14.274l-.08.654c-.045.484.23.634.61.332l1.599-1.337a3 3 0 0 0 .834-1.45s.078-.412.23-1.05a32 32 0 0 1 4.241-9.996c.402-.624.827-1.224 1.264-1.811q.427-.573.873-1.122a30 30 0 0 1 1.059-1.233 32 32 0 0 1 .903-.958 32 32 0 0 1 1.983-1.856 32 32 0 0 1 1.476-1.2c.045-.033.086-.07.131-.103.522-.392.866-.624.866-.624.111-.074.224-.176.335-.291.293-.303.56-.71.667-1.046l.066-.224.542-1.817q.025-.102.031-.184c.02-.33-.209-.441-.571-.238zm1.251-9.115-.376.164c-2.115.927-5.36 2.853-5.36 2.853l-1.38.857c-.339.222-.567.076-.509-.322l.207-1.62.08-.814.152-1.831.138-3.72-.01-.408c-.023-.447-.394-.626-.823-.398l-1.133.624c-.423.24-.758.816-.747 1.278v.422c.002 1.423-.209 3.163-.515 4.932l-.805 3.94-.856 3.433-.538 1.926c-.138.466.047.585.413.265l.133-.115 1.562-1.317 1.418-1.149 1.181-.927.521-.398 1.108-.825.905-.65 2.441-1.618.564-.338.515-.291.585-.31.472-.232.107-.049.283-.133a1.2 1.2 0 0 0 .308-.203c.294-.26.523-.662.556-1.026l.088-1.132.023-.291c.029-.486-.287-.744-.704-.574l-.002-.004zm.575-9.007c-.291-.183-2.236.675-2.236.675-.445.197-.776.712-.735 1.146l.027.394.088 1.651c.017.486.398.73.846.543l1.234-.492.368-.14c.409-.141.751-.654.758-1.14 0 0 .035-2.389-.351-2.637zm-15.245 9.006c.033-.486-.246-.628-.62-.318l-1.55 1.343c-.361.328-.718.987-.793 1.468l-.179.976a31.9 31.9 0 0 1-4.461 11.015 32 32 0 0 1-7.744 8.323c-.499.375-.795.574-.795.574a2.8 2.8 0 0 0-.975 1.347l-.511 1.946c-.111.474.144.666.567.425 0 0 .109-.06.542-.324a34.5 34.5 0 0 0 11.127-10.866 34.6 34.6 0 0 0 5.331-15.265l.06-.644.002.002zM62.712 60.64s-6.736 5.941-11.075 8.019l-.384.183c-.427.191-.803.746-.838 1.231l-.072 1.181c-.023.486.296.738.712.56l.374-.17c1.965-.886 4.937-2.658 4.937-2.658a52 52 0 0 0 1.381-.859c.341-.223.571-.076.509.328s-.156 1.13-.209 1.612l-.372 5.509v.414c.008.455.367.642.797.414l1.168-.634c.425-.236.772-.816.772-1.288l.012-.429c.121-4.729 2.697-13.155 2.697-13.155.142-.464-.039-.584-.406-.261l-.002.004z"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          fill: "#7e8859",
          d: "M40.084 28.237c-.16-.582-.386-.572-.503.02l-.322 1.926a38 38 0 0 0-.033 10.337 38 38 0 0 0 2.568 9.527c.384.927.78 1.735.78 1.735.263.541.487.492.495-.111 0 0 .107-7.377-.618-12.603-.634-4.565-2.365-10.831-2.365-10.831zm.324 32.778a44.4 44.4 0 0 1-7.661-19.627 44.4 44.4 0 0 1 2.449-21.606l.248-.65c.232-.556.175-1.436-.125-1.955s-1.32-1.389-1.572-.843c0 0-.004.006-.22.513a47.04 47.04 0 0 0-3.398 24.892 47.04 47.04 0 0 0 9.38 22.533l.339.439c.38.466.837.388 1.012-.172s.308-2.476-.053-2.959c0 0-.056-.076-.398-.564l-.002-.002zM29.125 8.385c-.328-.41-1.414-1.099-1.679-.626l-.232.433-.965 1.905-.745 1.575c-.15.324-.458.365-.686.092l-1.135-1.319-1.694-1.858-.341-.355c-.382-.384-.768-.525-.86-.314l-.082.191-.45 1.05c-.236.554-.117 1.329.265 1.723l.337.367c1.864 2.034 4.473 5.642 4.473 5.642.353.488.813.425 1.024-.139l2.665-6.294.226-.447c.259-.484.203-1.216-.125-1.626zM16.199 32.308l-.747-1.511c-.285-.531-.521-.472-.526.131 0 0-.068 7.541.564 12.096a57 57 0 0 0 .741 4.077l.329 1.47.415 1.721.374 1.458.62 2.274.152.535.314 1.071a2 2 0 0 0 .068.187c.096.217.201.283.294.203.068-.059.131-.197.174-.416 0 0 .094-.459.201-1.101l.093-.591q.016-.117.031-.236l.142-1.069q.081-.7.134-1.407a39 39 0 0 0 .092-1.721q.024-.748.02-1.503a38 38 0 0 0-.329-4.785 38 38 0 0 0-3.157-10.882zm20.624 39.175c-1.591-1.807-3.777-4.853-3.777-4.853-.351-.488-.809-.427-1.02.137L29.62 72.53l-.22.451c-.252.49-.187 1.226.142 1.633s1.419 1.091 1.679.615l.228-.437.686-1.368.743-1.575c.148-.324.458-.365.686-.092l1.131 1.323.979 1.091.335.365c.378.394.858.32 1.069-.164s.45-2.096.078-2.506l-.333-.379zM18.255 18.405l-.343-.422c-.39-.453-.844-.363-1.008.201s-.248 2.457.123 2.931l.406.543a44.4 44.4 0 0 1 8.4 20.659 44.6 44.6 0 0 1 .185 10.257 42 42 0 0 1-.253 2.112 44 44 0 0 1-.489 2.736 46 46 0 0 1-.731 2.949 44 44 0 0 1-1.065 3.282l-.23.619a2.43 2.43 0 0 0 .117 1.862c.012.022.021.047.035.066a2.8 2.8 0 0 0 .951.863c.283.144.538.168.645-.07l.211-.501a47 47 0 0 0 2.217-6.616 47 47 0 0 0 .721-3.341c.838-4.695.975-9.596.308-14.572a47.04 47.04 0 0 0-10.197-23.559zm62.422 32.693-.318.258-.743.63-.144.127-.899.835-.667.66-.889.952-1.043 1.22-.856 1.112-1.08 1.565a30.7 30.7 0 0 0-3.408 7.211l-.429 1.477c-.121.47.043.558.363.191 0 0 3.929-4.485 6.218-8.083l.425-.687a52 52 0 0 0 .706-1.208l.774-1.409.612-1.165a102 102 0 0 0 1.507-3.038l.189-.4c.205-.441.06-.554-.32-.252zm2.65-10.397-.404.201a38 38 0 0 0-3.782 2.158l-1.753 1.202-.932.701-1.724 1.426-1.236 1.134-1.382 1.399-1.125 1.245a38 38 0 0 0-3.186 4.274 38.04 38.04 0 0 0-6.023 18.787l-.021.447c-.01.486.312.677.714.425s1.486-1.362 1.517-1.848l.051-.556A35.9 35.9 0 0 1 69.6 55.598a36 36 0 0 1 2.272-3.142 37 37 0 0 1 .994-1.183 35 35 0 0 1 2.93-2.964l2.141-1.786 1.374-1.001 3.106-1.938.491-.269a2.03 2.03 0 0 0 .948-1.269c.016-.088.023-.195.022-.312-.008-.515-.187-1.198-.548-1.03l-.002-.004zm1.078-8.257c-.123-.234-.285-.394-.466-.326l-.368.146-1.316.546-.275.121-1.283.578c-.197.09-.365.037-.435-.119a.4.4 0 0 1-.035-.185l-.008-1.407-.07-2.031-.023-.398c-.035-.437-.199-.726-.365-.644l-.15.074-.827.41c-.435.217-.768.757-.739 1.2l.016.404c.039 1.06-.004 2.428-.061 3.571l-.097 1.661-.043.587c-.039.486.277.689.702.453l4.935-2.477.372-.156c.265-.105.503-.342.643-.609a1.06 1.06 0 0 0 .131-.457c.008-.236-.086-.646-.238-.941zM53.677 57.116l1.066-.886a30.7 30.7 0 0 0 5.666-6.551 30.7 30.7 0 0 0 3.753-8.353l.331-1.321c.103-.476-.074-.564-.392-.197 0 0-3.991 4.606-5.986 7.74-2.351 3.696-4.785 9.303-4.785 9.303-.193.447-.039.566.343.265h.002zm2.191 13.724-4.498 2.285-.372.162c-.411.17-.757.656-.766 1.079s.302 1.417.712 1.259l.368-.15 1.139-.482 1.281-.58c.261-.121.472.016.472.302l.004 1.407.031 1.185.016.4c.027.439.361.646.743.459s1.375-1.054 1.361-1.501l-.008-.408c-.033-1.948.217-4.969.217-4.969.039-.486-.275-.687-.7-.451l.002.002zm-4.448-3.548.392-.199a37.96 37.96 0 0 0 14.839-13.395 38.2 38.2 0 0 0 3.777-7.627 38 38 0 0 0 1.647-6.044 38 38 0 0 0 .634-6.178l.01-.439c-.004-.484-.329-.666-.725-.404s-1.439 1.382-1.459 1.87c0 0-.002.07-.033.546a35.9 35.9 0 0 1-5.645 17.121 35.84 35.84 0 0 1-12.621 11.901l-.466.26c-.433.224-.844.794-.916 1.267s.131 1.532.567 1.325l-.002-.002z"
        }
      ),
      /* @__PURE__ */ jsx("g", { fill: "#000", children: /* @__PURE__ */ jsx("path", { d: "M99.285 41.473c-.688-2.102-1.827-3.923-3.387-5.416-2.472-2.361-5.76-3.655-9.371-3.718l.035-1.735c.055-2.918.027-4.477-1.185-5.259-.503-.318-1.063-.4-1.735-.289-.842.139-1.862.58-3.178 1.241l-1.536.765-.138.07c-2.102 1.04-4.276 2.117-6.362 3.384-6.134 3.729-11.474 9.124-15.871 16.034-7.891 12.402-8.049 20.776-8.214 29.644-.045 2.469.439 3.895 1.572 4.623 1.168.745 2.732.566 5.075-.584 7.955-3.899 15.467-7.58 23.36-19.984l.462-.742a15.3 15.3 0 0 0 6.824 1.706c.41.01.819 0 1.217-.025 7.46-.494 12.987-6.729 13.145-14.826a14.8 14.8 0 0 0-.71-4.891h-.002zM82.43 52.788a8.1 8.1 0 0 0 3.519.847h.086q.27.01.532-.008c.023 0 .047-.006.072-.006l.651 3.189a9 9 0 0 1-1.577.096 11.1 11.1 0 0 1-4.751-1.128 54 54 0 0 0 1.468-2.988zm2.855-8.396c.31-.047.62-.064.918-.047.692.039 1.242.254 1.55.605q.378.432.32 1.187c-.043.578-.322 2.5-2.057 2.945a2 2 0 0 1-.318.049c-.499.033-1.094-.115-1.65-.406a42 42 0 0 0 1.234-4.332h.002zm-1.683 5.574c.922.447 1.913.595 2.739.383 1.714-.439 2.876-2.016 3.036-4.112.064-.851-.156-1.594-.641-2.147-.55-.626-1.4-.989-2.461-1.05a6 6 0 0 0-.692.004l.275-1.667.215-.02c1.622-.107 3.003.297 3.882 1.138.823.786 1.242 2.002 1.213 3.513-.068 3.481-2.04 6.136-4.687 6.311q-.2.014-.415.008h-.099a6.9 6.9 0 0 1-3.012-.734q.347-.825.649-1.63v.002zm3.685 3.564a6 6 0 0 0 .458-.111l1.133 3.021a10 10 0 0 1-.946.258l-.645-3.165zm1.072-.334q.21-.091.411-.199l1.581 2.8a9 9 0 0 1-.864.412l-1.129-3.011zm.971-.539c1.866-1.284 3.088-3.714 3.145-6.622a8 8 0 0 0-.027-.851l3.149-.572a11 11 0 0 1 .094 1.665c-.078 4.006-1.93 7.373-4.773 9.188l-1.585-2.808h-.002zm3.036-8.122a7 7 0 0 0-.088-.433l2.999-1.007a11 11 0 0 1 .215.872l-3.124.566zm-.275-1.06a6 6 0 0 0-.201-.482l2.776-1.452a9 9 0 0 1 .406.933l-2.979 1.001zm-.517-1.054a4.7 4.7 0 0 0-.716-.87c-1.137-1.087-2.847-1.618-4.816-1.499.142-1.148.244-2.279.314-3.402 2.562.025 4.869.904 6.567 2.527a8.5 8.5 0 0 1 1.427 1.792zm-9.437-14.838s1.946-.859 2.236-.675c.386.248.351 2.637.351 2.637-.008.486-.349.999-.758 1.14l-.368.141c-.69.261-1.234.492-1.234.492-.448.189-.829-.055-.846-.543 0 0-.027-.806-.088-1.651l-.027-.394c-.041-.433.291-.95.735-1.146zm-4.079 3.226-.016-.404c-.029-.443.304-.984.739-1.2l.827-.41.15-.074c.166-.082.329.207.365.644l.023.398c.056.972.07 2.032.07 2.032l.008 1.407q0 .109.035.185c.07.154.24.209.435.119l1.283-.578.275-.121 1.316-.547.368-.146c.181-.068.343.092.466.326a2.2 2.2 0 0 1 .238.941 1.06 1.06 0 0 1-.131.457c-.14.267-.378.505-.643.609l-.372.156c-1.963.824-4.935 2.477-4.935 2.477-.425.236-.741.033-.702-.453l.043-.587.097-1.661c.057-1.144.099-2.512.061-3.571zm-4.017 1.77c-.014-.462.324-1.038.747-1.278l1.133-.625c.429-.228.799-.051.823.398l.01.408c.023 1.095-.051 2.504-.138 3.72l-.152 1.831-.08.814c-.053.484-.144 1.21-.207 1.614s.17.55.509.328a47 47 0 0 1 1.38-.857s3.244-1.926 5.36-2.853l.376-.164c.417-.17.733.088.704.574l-.023.291-.088 1.132c-.033.365-.259.767-.556 1.026a1.3 1.3 0 0 1-.308.203l-.283.133-.107.049q-.231.109-.472.232-.287.148-.585.31l-.515.291-.564.338a46 46 0 0 0-2.441 1.618l-.905.65-1.107.825-.521.398-1.182.927-1.417 1.15-1.562 1.317-.133.115c-.365.32-.552.203-.413-.265 0 0 .222-.745.538-1.926a100 100 0 0 0 .856-3.433c.288-1.247.573-2.601.805-3.94.306-1.77.517-3.509.515-4.932v-.422zM53.331 56.85s2.433-5.607 4.785-9.303c1.995-3.134 5.986-7.74 5.986-7.74.32-.367.495-.279.392.197 0 0-.129.597-.332 1.321a30.8 30.8 0 0 1-3.753 8.353 30.8 30.8 0 0 1-5.666 6.551c-.573.5-1.067.886-1.067.886-.382.3-.538.18-.343-.265zM51.5 62.586l.511-1.946c.133-.468.571-1.073.975-1.347 0 0 .296-.201.795-.574a32 32 0 0 0 7.744-8.323 32 32 0 0 0 4.461-11.014l.179-.976c.074-.482.433-1.142.794-1.468l1.55-1.343c.374-.31.653-.168.62.318 0 0-.01.135-.061.644a34.54 34.54 0 0 1-5.331 15.265A34.6 34.6 0 0 1 52.612 62.69l-.542.324c-.423.24-.678.049-.567-.425zm-.649 3.38c.072-.472.486-1.042.916-1.266 0 0 .053-.027.466-.26a35.86 35.86 0 0 0 12.621-11.901 35.9 35.9 0 0 0 5.645-17.121l.033-.546c.019-.486 1.063-1.608 1.458-1.87s.723-.078.725.404l-.01.439a38 38 0 0 1-.634 6.179 38 38 0 0 1-1.647 6.044 38.2 38.2 0 0 1-3.777 7.627A38 38 0 0 1 51.808 67.09l-.392.199c-.437.207-.642-.853-.567-1.325zm1.314 13.327s-.854.221-1.25-.031-.542-1.083-.542-1.083c-.086-.478.17-.991.569-1.138l.359-.142.76-.305c.449-.187.831.059.846.544l.037.773.02.388c.031.425-.33.872-.799.993zm4.184-3.037.008.408c.014.447-.977 1.313-1.361 1.501s-.717-.019-.743-.459l-.016-.4-.031-1.185-.004-1.407c0-.287-.211-.424-.472-.302l-1.281.58s-.483.217-1.139.482l-.369.15c-.409.158-.721-.835-.712-1.259s.355-.91.766-1.079l.372-.162c1.809-.783 4.498-2.285 4.498-2.285.425-.238.739-.035.7.451 0 0-.25 3.021-.216 4.969l-.002-.002zm4.059-1.772c0 .472-.347 1.052-.772 1.288l-1.168.634c-.431.226-.79.039-.797-.414v-.414c-.004-2.127.372-5.509.372-5.509.055-.484.148-1.21.209-1.612s-.168-.55-.509-.328-.961.609-1.38.859c0 0-2.971 1.772-4.937 2.658l-.374.17c-.415.18-.735-.072-.712-.56l.072-1.181c.035-.486.411-1.04.838-1.231l.384-.183c4.34-2.078 11.075-8.019 11.075-8.019.365-.322.548-.205.406.261 0 0-2.576 8.425-2.697 13.155l-.012.429.002-.004zm3.578-2.234c-.031.486-1.115 1.596-1.517 1.848s-.723.062-.714-.425l.021-.447a38 38 0 0 1 6.023-18.787 38 38 0 0 1 3.186-4.274 40 40 0 0 1 1.125-1.245 37 37 0 0 1 2.618-2.533q.843-.741 1.724-1.427a35 35 0 0 1 .932-.701c.573-.42 1.16-.818 1.753-1.202a39 39 0 0 1 3.783-2.158l.404-.201c.361-.168.54.515.548 1.031 0 .117-.006.224-.021.312-.088.478-.513 1.048-.948 1.269 0 0-.07.035-.491.269a35 35 0 0 0-3.106 1.938q-.697.485-1.375 1.001a36 36 0 0 0-2.141 1.786 35 35 0 0 0-1.718 1.653 35 35 0 0 0-1.213 1.311q-.509.58-.994 1.183a36 36 0 0 0-2.272 3.142 35.9 35.9 0 0 0-5.559 16.098l-.051.556zm4.467-3.466c-.092.478-.468 1.13-.835 1.45l-1.599 1.337c-.382.303-.655.152-.61-.332 0 0 .014-.142.08-.654a34.6 34.6 0 0 1 5.221-14.274 36 36 0 0 1 1.843-2.597q.46-.59.944-1.153a38 38 0 0 1 1.141-1.274q.639-.68 1.306-1.317a34 34 0 0 1 1.583-1.419 35 35 0 0 1 1.807-1.425 34 34 0 0 1 1.811-1.247l.817-.507.581-.345c.363-.203.591-.092.571.238a1 1 0 0 1-.031.184l-.542 1.817-.066.224c-.107.336-.374.743-.667 1.046a2.2 2.2 0 0 1-.335.291s-.343.232-.866.624c-.045.033-.086.07-.131.103q-.754.576-1.476 1.2a32 32 0 0 0-1.983 1.856 32 32 0 0 0-.903.958 32 32 0 0 0-1.932 2.355 32 32 0 0 0-1.263 1.811 32 32 0 0 0-4.241 9.996c-.15.638-.23 1.05-.23 1.05zm8.329-9.529c-2.289 3.599-6.218 8.083-6.218 8.083-.32.365-.484.279-.363-.191 0 0 .179-.704.429-1.477a30.8 30.8 0 0 1 3.408-7.211c.345-.537.708-1.058 1.08-1.565a30 30 0 0 1 .856-1.112q.507-.626 1.043-1.22a30 30 0 0 1 1.556-1.612 28 28 0 0 1 .899-.835l.144-.127.743-.63.318-.258c.38-.303.525-.189.32.252l-.189.4a110 110 0 0 1-1.507 3.038l-.612 1.165-.774 1.409-.706 1.208-.425.687zm9.975.539a13 13 0 0 1-1.098.023 13.95 13.95 0 0 1-6.124-1.507l.799-1.384a11 11 0 0 0 .608.281 14 14 0 0 0 .602.238q.1.036.203.07.206.074.415.139.12.036.244.07l.388.107.257.062.388.084q.131.026.263.049.2.034.402.063l.254.035a11 11 0 0 0 .445.043l.218.02a13 13 0 0 0 .669.025l.16.002q.401.004.803-.021a11 11 0 0 0 .638-.061l.209-.031.415-.064q.123-.025.246-.053l.365-.08q.128-.034.255-.07c.113-.031.228-.06.339-.096q.13-.043.257-.088l.324-.113.257-.103.308-.129c.086-.037.168-.08.251-.119q.15-.07.294-.144.124-.066.246-.135l.283-.16q.121-.074.24-.15.137-.084.271-.174.117-.08.234-.164l.259-.189.224-.178a8 8 0 0 0 .248-.203q.11-.094.216-.191l.234-.217q.104-.1.207-.203l.222-.23a9 9 0 0 0 .197-.217 8 8 0 0 0 .21-.242l.185-.226.199-.256.174-.238c.031-.045.064-.086.096-.131a6 6 0 0 0 .175-.264c1.263-1.938 2.012-4.33 2.065-6.971a12.3 12.3 0 0 0-.238-2.672 10 10 0 0 0-.064-.293l-.043-.164-.057-.219a7 7 0 0 0-.096-.33l-.062-.205q-.059-.177-.123-.351c-.022-.057-.039-.113-.061-.17a10 10 0 0 0-.209-.507 12 12 0 0 0-.209-.441 9 9 0 0 0-.215-.404l-.076-.129-.158-.265q-.047-.076-.096-.148l-.156-.234-.107-.152-.164-.223-.113-.146-.177-.218-.115-.137q-.1-.116-.205-.228l-.103-.111a9 9 0 0 0-.324-.326c-1.928-1.842-4.525-2.847-7.397-2.888l.062-1.604c3.262.055 6.225 1.212 8.442 3.331a11.6 11.6 0 0 1 3.024 4.838c.456 1.393.673 2.882.641 4.428-.144 7.354-5.124 13.013-11.841 13.458h-.004zM28.708 4.233C26.423 1.44 25.148-.008 23.379.23c-.729.105-1.322.476-1.866 1.12-.682.806-1.291 2.039-2 3.722l-.835 1.953-.076.178-3.281 8.294c-2.79 8.437-3.5 17.804-2.106 27.843a86 86 0 0 0 .577 3.593q-.104-.002-.213 0c-1.7.02-3.336.312-4.861.866-2.076.755-3.861 1.954-5.299 3.562C.86 54.222-.328 58.069.078 62.196c.047.474.113.947.197 1.395.77 4.467 3.129 8.349 6.473 10.646 2.925 2.008 6.44 2.73 9.899 2.031a15 15 0 0 0 2.108-.591 15.1 15.1 0 0 0 5.746-3.776c1.525 2.049 3.116 3.993 4.726 5.962 1.934 2.361 3.533 3.316 5.184 3.095 1.696-.236 3.026-1.661 4.313-4.625 4.369-10.056 8.497-19.554 5.994-37.575S35.653 12.727 28.704 4.239zm-6.59.705s1.145-2.371 1.566-2.432c.563-.076 2.445 2.203 2.445 2.203.382.464.472 1.222.199 1.682l-.234.427c-.439.8-.768 1.454-.768 1.454-.271.537-.825.613-1.23.168 0 0-.673-.738-1.406-1.485l-.343-.349c-.386-.377-.487-1.128-.226-1.669h-.002zM20.26 9.157l.45-1.05.082-.191c.09-.211.476-.068.86.314l.341.355c.833.868 1.694 1.858 1.694 1.858l1.135 1.319c.228.273.538.23.686-.092l.745-1.575s.458-.954.965-1.905l.232-.433c.265-.472 1.351.217 1.679.626s.382 1.142.125 1.626l-.226.447c-1.189 2.354-2.665 6.294-2.665 6.294-.211.564-.673.626-1.024.139 0 0-2.609-3.608-4.473-5.642l-.337-.367c-.382-.394-.501-1.169-.265-1.723zm-2.086 5.181.567-1.497c.22-.56.714-.689 1.094-.287l.335.377c1.981 2.229 4.748 6.294 4.748 6.294a57 57 0 0 1 1.098 1.684c.265.429.599.384.743-.1a56 56 0 0 1 .614-1.916s1.513-4.418 2.763-6.988l.222-.457c.255-.496.762-.505 1.123-.023l1.035 1.431c.349.492.425 1.325.172 1.85l-.22.484c-2.533 5.552-4.19 16.85-4.19 16.85-.088.595-.357.632-.601.082 0 0-4.761-10.802-8.844-15.583l-.339-.398c-.384-.426-.528-1.237-.322-1.803l.002.002zm-1.269 3.848c.164-.564.618-.654 1.008-.201l.343.421a47.04 47.04 0 0 1 10.197 23.559 47.2 47.2 0 0 1-1.029 17.913 47 47 0 0 1-2.217 6.616l-.211.502c-.107.236-.363.213-.645.07-.367-.187-.776-.578-.951-.863q-.019-.033-.035-.066c-.279-.509-.335-1.327-.117-1.862 0 0 .027-.068.23-.619a45 45 0 0 0 1.064-3.282 44 44 0 0 0 .731-2.949 44 44 0 0 0 .489-2.736c.103-.699.183-1.405.253-2.111a44.4 44.4 0 0 0-.185-10.257 44.4 44.4 0 0 0-8.399-20.659l-.406-.543c-.37-.474-.287-2.367-.123-2.931zm.261 40.807c-.778-.851-1.965-1.376-3.375-1.438-.852-.037-1.587.207-2.123.71-.608.57-.944 1.432-.969 2.494a6 6 0 0 0 .025.693c.135 1.364.739 2.738 1.605 3.569.626.601 1.353.892 2.102.841 1.022-.068 2.028-.704 2.757-1.747.634-.906.959-1.957.922-2.894q.603 1.331 1.246 2.568c-.875 1.928-2.591 3.433-4.636 3.844-.965.195-1.874.006-2.702-.564-1.427-.982-2.474-2.884-2.872-5.228l-.014-.074a8 8 0 0 1-.107-.751c-.158-1.62.201-3.013 1.012-3.921.76-.849 1.96-1.306 3.469-1.325a8.5 8.5 0 0 1 2.615.365 56 56 0 0 0 1.043 2.857h.004zm-1.049 4.371c-.501.716-1.148 1.151-1.772 1.192-.378.025-.753-.137-1.111-.48-.741-.71-1.258-2.092-1.23-3.286.018-.693.215-1.251.556-1.569q.42-.39 1.174-.359c.579.025 2.505.242 3.005 1.963a1.6 1.6 0 0 1 .059.316c.066.669-.187 1.518-.681 2.223h.002zm-.464-8.698a10 10 0 0 0-2.16-.203 9 9 0 0 0-.848.055l-.671-3.13a11 11 0 0 1 1.659-.146c.368-.004.729.012 1.088.037q.438 1.769.932 3.39zm-3.656-.047q-.22.044-.431.103l-1.1-2.968a10 10 0 0 1 .866-.242zm-1.049.308a5 5 0 0 0-.474.217l-1.538-2.73q.444-.242.918-.435l1.094 2.949zm-1.037.55a4.7 4.7 0 0 0-.846.743c-1.059 1.185-1.534 2.931-1.339 4.92.027.285.07.576.123.861l.012.064q.078.455.185.888l-3.311.905c-.084-.338-.16-.681-.218-1.03a12 12 0 0 1-.15-1.062c-.285-2.912.519-5.589 2.264-7.539a8.5 8.5 0 0 1 1.745-1.485l1.538 2.732-.002.002zm-1.694 8.109a14 14 0 0 0 .164.498l-3.207 1.212a14 14 0 0 1-.267-.806l3.311-.906v.002zm.402 1.107a10 10 0 0 0 .201.443l-3.049 1.522a13 13 0 0 1-.353-.753zm.503 1.023c.579 1.017 1.31 1.852 2.156 2.434 1.131.777 2.375 1.034 3.699.767q.335-.068.661-.168l.87 3.115c-.234.064-.47.125-.712.172a8.58 8.58 0 0 1-6.612-1.36c-1.244-.855-2.297-2.037-3.106-3.443l3.042-1.518zm7.134 2.812a6 6 0 0 0 .363-.158l1.535 2.888q-.497.232-1.02.414l-.877-3.144zm.94-.462a8 8 0 0 0 .575-.371l1.903 2.693q-.453.31-.938.574zm1.092-.771a8.5 8.5 0 0 0 1.811-2.129 55 55 0 0 0 1.876 3.107 11.3 11.3 0 0 1-1.774 1.731l-1.913-2.707zm-2.971-41.363.384-2.51c.103-.593.482-.683.838-.199 0 0 .099.135.458.656a42.75 42.75 0 0 1 7.208 18.657 43 43 0 0 1 .144 10.234q-.12 1.126-.294 2.236a43 43 0 0 1-.366 2.004l-.031.15a35 35 0 0 1-.176.781l-.238.974-.189.718-.174.607a40 40 0 0 1-.384 1.247l-.068.217-.25.74c-.207.566-.601.589-.875.055l-.123-.256-.955-1.987c-.25-.548-.322-1.471-.162-2.051 0 0 .084-.306.213-.853l.076-.326.168-.775a37 37 0 0 0 .273-1.473l.179-1.169a39 39 0 0 0 .174-1.505 40 40 0 0 0 .137-1.823 38 38 0 0 0 .049-1.577 40 40 0 0 0-.351-5.736 39.6 39.6 0 0 0-4.621-13.955c-.376-.679-.612-1.064-.612-1.064a3.6 3.6 0 0 1-.429-2.02l-.002.002zm.172 17.09c-.632-4.553-.564-12.096-.564-12.096.006-.603.242-.662.526-.131a31 31 0 0 0 .747 1.511 38 38 0 0 1 3.157 10.882c.216 1.608.32 3.205.329 4.785q.007.754-.02 1.503a39 39 0 0 1-.226 3.128 39 39 0 0 1-.142 1.069c-.012.078-.019.158-.031.236l-.094.591-.201 1.101q-.069.328-.174.416c-.094.08-.199.014-.294-.203a2 2 0 0 1-.068-.187l-.314-1.071-.152-.535-.62-2.274-.374-1.458-.415-1.721-.33-1.47a58 58 0 0 1-.741-4.077zm2.79 31.336a13 13 0 0 1-1.905.535c-3.086.623-6.224-.022-8.832-1.813-3.036-2.086-5.18-5.634-5.888-9.738a15 15 0 0 1-.181-1.288c-.367-3.731.696-7.199 2.993-9.765a11.6 11.6 0 0 1 4.736-3.179c1.377-.5 2.858-.763 4.401-.782l.464.004.345 1.614a13 13 0 0 0-.79-.014 12 12 0 0 0-2.661.324l-.29.074-.162.047-.216.062q-.163.051-.328.105-.102.034-.203.07a11 11 0 0 0-.347.135l-.168.066q-.252.106-.499.224a12 12 0 0 0-.435.222q-.201.11-.396.228-.063.038-.127.08l-.259.166-.146.102a6 6 0 0 0-.228.164l-.148.111-.216.17-.142.117q-.107.091-.213.185-.068.058-.133.119l-.22.213-.109.105a10 10 0 0 0-.314.334c-1.993 2.227-2.913 5.257-2.591 8.538q.021.219.051.441a14 14 0 0 0 .113.722c.415 2.416 1.377 4.61 2.747 6.356a11.3 11.3 0 0 0 2.47 2.338 10 10 0 0 0 1.795.983c.076.033.154.059.232.09.156.061.31.123.468.176.094.031.189.057.285.086.144.043.287.088.431.125q.152.037.308.068.209.048.421.086.16.028.322.047l.415.053a10 10 0 0 0 .332.023l.415.02q.167.001.337 0a7 7 0 0 0 .415-.014q.17-.01.339-.025c.07-.006.138-.008.209-.016q.194-.021.388-.051c.175-.027.337-.055.499-.088a11 11 0 0 0 .71-.168c.08-.022.158-.049.236-.07.154-.045.306-.088.456-.138.092-.031.181-.068.273-.102.134-.049.269-.096.402-.15q.142-.06.283-.123l.372-.166.285-.144.349-.181c.096-.053.187-.109.281-.164l.331-.199q.136-.09.273-.181l.316-.217c.09-.064.175-.133.263-.199l.3-.232q.129-.106.253-.215l.285-.248q.122-.112.244-.23l.269-.263q.117-.12.23-.244l.253-.277.944 1.343a13.74 13.74 0 0 1-5.387 3.606l-.004.008zm17.037 3.347s-.628.894-1.201.972h-.004c-.567.076-1.375-.585-1.375-.585-.464-.382-.634-1.069-.376-1.528l.226-.421.472-.896c.273-.537.829-.611 1.232-.166l.653.699.329.349c.37.375.39 1.085.043 1.579zm1.765-3.337c-.211.484-.69.558-1.069.164l-.335-.365-.979-1.091-1.131-1.323c-.228-.273-.538-.23-.686.092l-.743 1.575s-.283.591-.686 1.368l-.228.437c-.259.476-1.349-.207-1.679-.615s-.394-1.142-.142-1.633l.22-.451c1.076-2.19 2.406-5.763 2.406-5.763.209-.564.669-.626 1.02-.137 0 0 2.186 3.046 3.777 4.853l.333.379c.372.41.131 2.022-.078 2.506zm2.453-5.98-.593 1.534c-.224.558-.712.671-1.084.25l-.331-.388c-1.708-2.002-4.063-5.488-4.063-5.488a58 58 0 0 1-1.096-1.686c-.265-.429-.599-.385-.743.099s-.417 1.347-.612 1.916c0 0-1.379 4.055-2.519 6.467l-.218.461c-.248.502-.751.521-1.117.045l-.877-1.171a1.6 1.6 0 0 1-.22-.431c-.158-.459-.162-1.013.023-1.401l.215-.482c.998-2.24 1.852-5.423 2.515-8.394.946-4.243 1.494-8.05 1.494-8.05.086-.597.353-.63.591-.078 0 0 4.325 10.004 7.998 14.556l.333.414c.378.445.517 1.268.304 1.832l.002-.006zm1.328-3.85c-.175.56-.632.638-1.012.172l-.339-.439a47.06 47.06 0 0 1-9.38-22.533 47.1 47.1 0 0 1 3.398-24.892l.22-.513c.252-.546 1.271.324 1.572.843s.357 1.399.125 1.956c0 0-.037.09-.248.65a44.4 44.4 0 0 0-2.449 21.606 44.4 44.4 0 0 0 7.661 19.627l.398.564c.361.482.228 2.398.053 2.959l.002.002zm1.757-7.771-.435 2.543c-.117.591-.495.669-.842.178 0 0-.101-.146-.449-.681a42.76 42.76 0 0 1-6.516-17.636 42.8 42.8 0 0 1 1.839-19.338l.271-.79c.209-.564.591-.582.848-.037l1.063 2.412c.232.556.289 1.487.127 2.065 0 0-.139.496-.314 1.284a39.7 39.7 0 0 0-.606 13.973 39.6 39.6 0 0 0 4.014 12.82 31 31 0 0 0 .626 1.173c.296.525.466 1.44.376 2.036h-.002zm.452-5.096c-.01.601-.232.652-.495.111 0 0-.394-.808-.78-1.735a38 38 0 0 1-2.568-9.527 38 38 0 0 1 .033-10.337c.142-1.011.322-1.926.322-1.926.117-.591.343-.599.503-.02 0 0 1.731 6.264 2.365 10.831.725 5.226.618 12.603.618 12.603z" }) })
    ]
  }
);
function FirstNationsBanner() {
  return /* @__PURE__ */ jsxs(Columns, { space: "large", collapseBelow: "tablet", children: [
    /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(FirstNationsArtwork, {}) }),
    /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
      /* @__PURE__ */ jsxs(Text, { size: "small", weight: "strong", tone: "secondary", children: [
        /* @__PURE__ */ jsxs(Box, { display: { mobile: "inline", wide: "block" }, component: "span", children: [
          "SEEK acknowledges the Traditional Custodians of the lands on which it operates its online employment marketplace.",
          " "
        ] }),
        /* @__PURE__ */ jsx(Box, { display: { mobile: "inline", wide: "block" }, component: "span", children: "The gum leaf represents a symbol of welcome and it acknowledges the diverse countries, environments and communities." })
      ] }),
      /* @__PURE__ */ jsx(Text, { size: "small", tone: "secondary", children: "Artwork by Bitja, Dixon Patten Jnr, Gunnai, Gunditjmara, Yorta Yorta and Dhudhuroa, Bayila Creative." })
    ] }) })
  ] });
}
const UpperFooter = ({ localisations }) => {
  const mapSegment = useCallback(
    (key) => /* @__PURE__ */ jsx(Column, { width: "1/2", children: /* @__PURE__ */ jsx(FooterSegment, { segment: localisations.segments[key] }) }, key),
    [localisations.segments]
  );
  const { country } = useMelwaysInfo();
  const isFirstNationsBannerEnabled = country === "au";
  return /* @__PURE__ */ jsxs(Stack, { space: "xlarge", children: [
    /* @__PURE__ */ jsxs(Columns, { collapseBelow: "tablet", space: "medium", children: [
      /* @__PURE__ */ jsx(Column, { width: "1/2", children: /* @__PURE__ */ jsx(Columns, { space: "medium", children: [0, 1].map(mapSegment) }) }),
      /* @__PURE__ */ jsx(Column, { width: "1/2", children: /* @__PURE__ */ jsx(Box, { marginTop: ["small", "none"], children: /* @__PURE__ */ jsx(Columns, { space: "medium", children: [2, 3].map(mapSegment) }) }) })
    ] }),
    /* @__PURE__ */ jsx(Divider, {}),
    isFirstNationsBannerEnabled && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(FirstNationsBanner, {}),
      /* @__PURE__ */ jsx(Divider, {})
    ] })
  ] });
};
const linkContext = { source: "partner services" };
const partnerServices = {
  au: {
    linkContext,
    links: [
      {
        title: "SEEK Pass",
        url: "https://seekpass.co/",
        linkContext: { source: "seek pass" },
        openInNewWindow: true
      },
      {
        title: "GradConnection",
        url: "https://au.gradconnection.com/",
        linkContext: { source: "gradconnection" },
        openInNewWindow: true
      },
      {
        title: "Sidekicker",
        url: "https://sidekicker.com/au/",
        linkContext: { source: "sidekicker" },
        openInNewWindow: true
      },
      {
        title: "GO1",
        url: "https://www.go1.com/",
        linkContext: { source: "go1" },
        openInNewWindow: true
      },
      {
        title: "FutureLearn",
        url: "https://www.futurelearn.com/",
        linkContext: { source: "futurelearn" },
        openInNewWindow: true
      },
      {
        title: "JobAdder",
        url: "https://jobadder.com/",
        linkContext: { source: "jobadder" },
        openInNewWindow: true
      }
    ]
  },
  nz: {
    linkContext,
    links: [
      {
        title: "Sidekicker",
        url: "https://sidekicker.com/nz/",
        linkContext: { source: "sidekicker" },
        openInNewWindow: true
      }
    ]
  }
};
const loadPartnerServices = ({
  country = "au"
}) => partnerServices[country];
const usePartners = () => {
  const { brand } = useMelwaysInfo();
  const urlResolver = useMelwaysLink();
  const rootUrl = (expectedBrand) => urlResolver({
    brand: expectedBrand,
    country: null,
    path: "",
    absolute: true,
    language: "en"
  });
  const countryUrl = (country) => urlResolver({
    country,
    path: "",
    absolute: true,
    language: "en"
  });
  return {
    links: [
      {
        title: "Bdjobs (Bangladesh)",
        url: "https://www.bdjobs.com/",
        linkContext: { source: "bdjobs" },
        openInNewWindow: true
      },
      ...brand !== "jobsdb" ? [
        {
          title: "Jobsdb (SE Asia)",
          url: rootUrl("jobsdb"),
          linkContext: { source: "jobsdb" },
          openInNewWindow: true
        }
      ] : [],
      ...brand !== "jobstreet" ? [
        {
          title: "Jobstreet (SE Asia)",
          url: rootUrl("jobstreet"),
          linkContext: { source: "jobstreet" },
          openInNewWindow: true
        }
      ] : [],
      {
        title: "Jora (Australia)",
        url: "https://au.jora.com/",
        linkContext: { source: "jora au" },
        openInNewWindow: true
      },
      {
        title: "Jora (Worldwide)",
        url: "https://jora.com/",
        linkContext: { source: "jora global" },
        openInNewWindow: true
      },
      ...brand !== "seek" ? [
        {
          title: "SEEK (Australia)",
          url: countryUrl("au"),
          linkContext: { source: "seek" },
          openInNewWindow: true
        },
        {
          title: "SEEK (New Zealand)",
          url: countryUrl("nz"),
          linkContext: { source: "seek" },
          openInNewWindow: true
        }
      ] : []
    ],
    linkContext: { source: "international partners" }
  };
};
const useJobSeekersSegment = ({
  authenticationStatus = AUTHENTICATION_TYPES.UNAUTHENTICATED
}) => {
  const isAuthenticated = authenticationStatus === AUTHENTICATION_TYPES.AUTHENTICATED;
  const urls = useUrls();
  const authenticatedUrl = generateAuthenticatedUrl({
    isAuthenticated,
    loginUrl: urls.user.login
  });
  const { t } = useTranslations(translations$2);
  const { t: commonT } = useTranslations(translations$3);
  const { brand, country: melwaysCountry } = useMelwaysInfo();
  const brandDisplayName = brandDisplayNameMap[brand];
  const country = melwaysCountry || DEFAULT_COUNTRY;
  const countries = useCountries();
  return {
    title: t("Job seekers"),
    links: [
      {
        title: commonT("Job search"),
        url: urls.sites.jobs,
        linkContext: { source: "job search" }
      },
      {
        title: commonT("Profile"),
        url: getUserProfileLink({
          isAuthenticated,
          profileLinks: urls.user.profile
        }),
        linkContext: { source: "profile" }
      },
      {
        title: commonT("People search"),
        url: urls.sites.peopleSearch.footer,
        linkContext: { source: "people search" }
      },
      {
        title: commonT("Saved searches"),
        url: authenticatedUrl(urls.user.savedSearches),
        linkContext: { source: "saved searches" },
        shouldNoFollow: true
      },
      {
        title: commonT("Saved jobs"),
        url: authenticatedUrl(urls.user.savedJobs),
        linkContext: { source: "saved jobs" },
        shouldNoFollow: true
      },
      {
        title: commonT("Applied jobs"),
        url: authenticatedUrl(urls.user.appliedJobs),
        linkContext: { source: "applied jobs" },
        shouldNoFollow: true
      },
      ...country !== "hk" ? [
        {
          title: commonT("Career advice"),
          url: urls.careerAdvice.root,
          linkContext: { source: "career advice" }
        },
        {
          title: t("Explore careers"),
          url: urls.careerAdvice.exploreCareers,
          linkContext: { source: "explore careers" }
        },
        {
          title: t("Explore salaries"),
          url: urls.careerAdvice.exploreSalaries,
          linkContext: { source: "explore salaries" }
        }
      ] : [],
      {
        title: commonT("Companies"),
        url: urls.sites.companies,
        linkContext: { source: "company reviews" }
      },
      ...brand === "seek" && (country === "au" || country === "nz") ? [
        {
          title: commonT("Recruiters"),
          url: urls.sites.recruiters,
          linkContext: { source: "explore recruiters" }
        }
      ] : [],
      {
        title: commonT("Community"),
        url: urls.sites.community,
        linkContext: { source: "community" }
      },
      {
        title: t("Download apps"),
        linkContext: { source: "download apps" },
        links: [
          {
            title: brand === "seek" ? t("Android") : t("{brand} @ Google Play", {
              brand: brandDisplayName
            }),
            url: urls.apps.android,
            iconComponent: IconPlatformAndroid,
            linkContext: { source: "android" },
            openInNewWindow: true,
            shouldNoFollow: true
          },
          {
            title: brand === "seek" ? t("iOS") : t("{brand} @ App Store", {
              brand: brandDisplayName
            }),
            url: urls.apps.ios,
            iconComponent: IconPlatformApple,
            linkContext: { source: "ios" },
            openInNewWindow: true,
            shouldNoFollow: true
          }
        ]
      },
      ...brand === "seek" ? [
        {
          title: t("{brand} sites", { brand: brandDisplayName }),
          linkContext: { source: "seek sites" },
          links: [
            {
              title: commonT("Employer site"),
              url: urls.employer.root,
              linkContext: { source: "employer site" }
            },
            ...country === "au" ? [
              {
                title: t("{brand} {country}", {
                  brand: brandDisplayName,
                  country: "NZ"
                }),
                url: countries.nz.link,
                linkContext: { source: "seek nz" }
              }
            ] : [
              {
                title: t("{brand} {country}", {
                  brand: brandDisplayName,
                  country: "AU"
                }),
                url: countries.au.link,
                linkContext: { source: "seek au" }
              }
            ],
            {
              title: commonT("Volunteering"),
              url: urls.sites.volunteering,
              linkContext: { source: "volunteering" }
            },
            {
              title: commonT("Business"),
              url: urls.sites.businessForSale,
              linkContext: { source: "buisness" }
            }
          ]
        }
      ] : []
    ]
  };
};
const useEmployersSegment = () => {
  const { t } = useTranslations(translations$2);
  const urls = useUrls();
  return {
    title: t("Employers"),
    links: [
      {
        title: t("Register for free"),
        url: urls.employer.root,
        linkContext: { source: "register for free" },
        openInNewWindow: true
      },
      {
        title: t("Post a job ad"),
        url: urls.employer.root,
        linkContext: { source: "post a job ad" },
        openInNewWindow: true
      },
      {
        title: t("Products & prices"),
        url: urls.employer.productsAndPrices,
        linkContext: { source: "products & prices" },
        openInNewWindow: true
      },
      {
        title: t("Customer service"),
        url: urls.employer.customerService,
        linkContext: { source: "customer service" },
        openInNewWindow: true
      },
      {
        title: t("Hiring advice"),
        url: urls.employer.hiringAdvice,
        linkContext: { source: "hiring Advice" },
        openInNewWindow: true
      },
      {
        title: t("Market insights"),
        url: urls.employer.marketInsights,
        linkContext: { source: "market insights" },
        openInNewWindow: true
      },
      {
        title: t("Recruitment software partners"),
        url: urls.employer.recruitmentSoftwarePartners,
        linkContext: { source: "recruitment software partners" },
        openInNewWindow: true
      }
    ]
  };
};
const useAboutSegment = () => {
  const { brand, country: melwaysCountry } = useMelwaysInfo();
  const brandDisplayName = brandDisplayNameMap[brand];
  const country = melwaysCountry || DEFAULT_COUNTRY;
  const partnerServices2 = loadPartnerServices({
    // Using AU config as default for all non-NZ countries
    country: country === "nz" ? "nz" : "au"
  });
  const partners = usePartners();
  const urls = useUrls();
  const { t } = useTranslations(translations$2);
  return {
    title: t("About us"),
    links: [
      {
        title: brand === "seek" ? t("About {brand}", { brand: brandDisplayName }) : t("About us"),
        url: urls.about.aboutUs,
        linkContext: { source: "about seek" }
      },
      {
        title: t("Newsroom"),
        url: urls.about.newsroom,
        linkContext: { source: "newsroom" }
      },
      {
        title: t("Investors"),
        url: urls.about.investors,
        linkContext: { source: "investors" }
      },
      {
        title: t("Careers"),
        url: urls.about.careers,
        linkContext: { source: "work for seek" },
        openInNewWindow: true
      },
      {
        title: t("International partners"),
        links: partners.links,
        linkContext: partners.linkContext
      },
      {
        title: t("Partner services"),
        links: partnerServices2.links,
        linkContext: partnerServices2.linkContext
      }
    ]
  };
};
const useConnectSegment = () => {
  const { brand } = useMelwaysInfo();
  const brandDisplayName = brandDisplayNameMap[brand];
  const { t } = useTranslations(translations$2);
  const urls = useUrls();
  return {
    title: t("Contact"),
    links: [
      {
        title: t("Help centre"),
        url: urls.connect.helpCenter,
        linkContext: { source: "help centre" },
        openInNewWindow: true
      },
      {
        title: t("Contact us"),
        url: urls.connect.contactUs,
        linkContext: { source: "contact us" }
      },
      {
        title: t("Product & tech blog"),
        url: urls.connect.productAndTechBlog,
        linkContext: { source: "product & tech blog" },
        openInNewWindow: true
      },
      ...brand === "seek" ? [
        {
          title: t("{brand} videos", { brand: brandDisplayName }),
          url: urls.connect.videos,
          linkContext: { source: "seek videos" }
        }
      ] : [],
      {
        title: t("Social"),
        linkContext: { source: "social" },
        links: [
          {
            title: t("Facebook"),
            url: urls.social.facebook,
            iconComponent: IconSocialFacebook,
            linkContext: { source: "facebook" },
            openInNewWindow: true
          },
          {
            title: t("Instagram"),
            url: urls.social.instagram,
            iconComponent: IconSocialInstagram,
            linkContext: { source: "instagram" },
            openInNewWindow: true
          },
          {
            title: t("Twitter"),
            url: urls.social.twitter,
            iconComponent: IconSocialX,
            linkContext: { source: "twitter" },
            openInNewWindow: true
          },
          {
            title: t("YouTube"),
            url: urls.social.youtube,
            iconComponent: IconSocialYouTube,
            linkContext: { source: "youtube" },
            openInNewWindow: true
          }
        ]
      }
    ]
  };
};
const useLegalLinks = () => {
  const { t } = useTranslations(translations$2);
  const urls = useUrls();
  return [
    {
      title: t("Terms & conditions"),
      url: urls.legal.tnc,
      linkContext: { source: "terms & conditions" }
    },
    {
      title: t("Security"),
      url: urls.legal.security,
      linkContext: { source: "security" }
    },
    {
      title: t("Privacy"),
      url: urls.legal.privacy,
      linkContext: { source: "privacy" }
    }
  ];
};
const useLocalisations = ({
  authenticationStatus
}) => {
  const jobSeekersSegment = useJobSeekersSegment({
    authenticationStatus
  });
  const employersSegment = useEmployersSegment();
  const aboutSegment = useAboutSegment();
  const connectSegment = useConnectSegment();
  const legalLinks = useLegalLinks();
  const countryList = useCountryList();
  return {
    countryList,
    legalLinks,
    segments: [
      jobSeekersSegment,
      employersSegment,
      aboutSegment,
      connectSegment
    ],
    isSecondaryBackground: true
  };
};
const ApacCandidateFooterView = ({
  authenticationStatus = AUTHENTICATION_TYPES.UNAUTHENTICATED,
  data,
  isExpandedVersion = true,
  showCountryList = true,
  widthMode = "large"
}) => {
  const localisations = useLocalisations({
    authenticationStatus
  });
  const { t } = useTranslations(translations$2);
  const footerRef = useRef(null);
  const onOpenAccordion = useCallback(() => {
    var _a;
    const footerTop = ((_a = footerRef.current) == null ? void 0 : _a.offsetTop) ?? 0;
    const documentBottom = document.body.scrollHeight || document.documentElement.scrollHeight;
    window.scrollTo({
      top: footerTop || documentBottom
    });
  }, []);
  const upperFooter = /* @__PURE__ */ jsx(Box, { paddingTop: "xlarge", children: /* @__PURE__ */ jsx(UpperFooter, { localisations }) });
  const LowerFooter$1 = ({
    isFooterExpanded
  }) => /* @__PURE__ */ jsx(Box, { paddingTop: "large", paddingBottom: "xlarge", children: /* @__PURE__ */ jsx(
    LowerFooter,
    {
      localisations,
      showCountryList,
      isFooterExpanded
    }
  ) });
  const { isSecondaryBackground } = localisations;
  return /* @__PURE__ */ jsx(
    Box,
    {
      component: "footer",
      position: "relative",
      background: isSecondaryBackground ? "body" : "surface",
      ref: footerRef,
      data: { automation: "footer", ...data },
      children: /* @__PURE__ */ jsx(PageBlock, { width: widthMode, position: "footer", children: isExpandedVersion ? /* @__PURE__ */ jsxs(Fragment, { children: [
        upperFooter,
        /* @__PURE__ */ jsx(LowerFooter$1, {})
      ] }) : /* @__PURE__ */ jsx(
        Accordion,
        {
          callToAction: (isOpen) => /* @__PURE__ */ jsx(LowerFooter$1, { isFooterExpanded: isOpen }),
          onExpendCollapseLinkContextData: { source: "Collapsed Footer" },
          onOpen: onOpenAccordion,
          isInline: true,
          isDirectionUpward: true,
          isCallToActionFullWidth: true,
          actionDisplayText: t("Explore site"),
          isStickyAction: true,
          children: upperFooter
        }
      ) })
    }
  );
};
const ApacCandidateFooter = (props) => /* @__PURE__ */ jsx(CommonContextProvider, { position: "footer", children: /* @__PURE__ */ jsx(ApacCandidateFooterView, { ...props }) });
export {
  ApacCandidateFooter,
  ApacCandidateHeader,
  LinkProvider as ApacCandidateHeaderFooterLinkProvider,
  DATA_HEADER_FOOTER_LINK_CONTEXT_KEY,
  linkContextDestructor,
  mapLinkActionHelper
};
